import { Platform } from 'react-native';
import * as FileSystem from 'expo-file-system';
import { getYoutubeUploadChunkApiBase } from './apiConfig';
import { formatYoutubeUploadError } from './youtubeBinaryUpload';
import {
  YOUTUBE_UPLOAD_METHODS,
  formatBytes,
  logYoutubeUploadError,
  logYoutubeUploadPhase,
} from './youtubeUploadDebug';

/** Chunk size for YouTube resumable upload (must be a multiple of 256 KB). */
const DESKTOP_CHUNK_SIZE = 8 * 1024 * 1024;
const MOBILE_CHUNK_SIZE = 2 * 1024 * 1024;
const NATIVE_CHUNK_SIZE = 256 * 1024;
const MAX_CHUNK_RETRIES = 3;

function getChunkSize() {
  if (Platform.OS !== 'web') {
    return NATIVE_CHUNK_SIZE;
  }
  if (typeof navigator !== 'undefined') {
    if (/Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent || '')) {
      return MOBILE_CHUNK_SIZE;
    }
  }
  return DESKTOP_CHUNK_SIZE;
}

async function parseJsonResponse(response) {
  const text = await response.text();
  return parseJsonResponseFromText(text);
}

async function parseJsonResponseFromText(text) {
  if (!text) return {};
  try {
    return JSON.parse(text);
  } catch {
    const snippet = String(text).replace(/\s+/g, ' ').trim().slice(0, 160);
    return snippet ? { message: snippet } : {};
  }
}

function isRawNetworkFailure(err) {
  const msg = err?.message || String(err || '');
  return (
    /^(failed to fetch|load failed|network error|network request failed)$/i.test(msg.trim()) ||
    (/^typeerror:/i.test(msg) && /fetch|network|load failed/i.test(msg))
  );
}

async function postResumableChunk({
  method = YOUTUBE_UPLOAD_METHODS.WEB_RESUMABLE_FETCH,
  uploadUrl,
  mimeType,
  offset,
  end,
  total,
  body,
  chunkIndex,
}) {
  const chunkApi = getYoutubeUploadChunkApiBase();
  if (!chunkApi) {
    throw new Error('Video upload API URL is not configured.');
  }

  const chunkUrl = `${chunkApi}/api/youtube/upload-chunk`;
  let response;
  let lastErr;
  for (let attempt = 0; attempt < MAX_CHUNK_RETRIES; attempt += 1) {
    try {
      if (attempt > 0 || chunkIndex === 1) {
        logYoutubeUploadPhase(method, 'resumable-chunk-fetch', {
          chunkIndex,
          attempt: attempt + 1,
          chunkUrl,
          contentRange: `bytes ${offset}-${end}/${total}`,
          transport: 'fetch-binary',
        });
      }
      response = await fetch(chunkUrl, {
        method: 'POST',
        headers: {
          'Content-Type': mimeType,
          'X-Youtube-Upload-Url': uploadUrl,
          'Content-Range': `bytes ${offset}-${end}/${total}`,
        },
        body,
      });
      break;
    } catch (networkErr) {
      lastErr = networkErr;
      logYoutubeUploadError(method, 'resumable-chunk-fetch', networkErr, {
        chunkIndex,
        attempt: attempt + 1,
        chunkUrl,
        contentRange: `bytes ${offset}-${end}/${total}`,
      });
      if (!isRawNetworkFailure(networkErr) || attempt === MAX_CHUNK_RETRIES - 1) {
        const msg = networkErr?.message || String(networkErr);
        if (/network|failed to fetch|load failed/i.test(msg)) {
          throw new Error(
            'Lost connection while uploading the video. Check your network and try again.'
          );
        }
        throw networkErr;
      }
      await new Promise((r) => setTimeout(r, 800 * (attempt + 1)));
    }
  }
  if (!response) {
    throw lastErr || new Error('Video upload failed');
  }

  const data = await parseJsonResponse(response);
  if (!response.ok) {
    const err = new Error(formatYoutubeUploadError(data, 'Video upload failed', response.status));
    logYoutubeUploadError(method, 'resumable-chunk-http', err, {
      chunkIndex,
      chunkUrl,
      httpStatus: response.status,
    });
    throw err;
  }
  return data;
}

function chunkBodyNeedsStream(blob, total, chunkSize) {
  if (!total) return true;
  if (!(blob instanceof Blob)) return false;
  const end = Math.min(chunkSize, total) - 1;
  if (end < 0) return true;
  return blob.slice(0, end + 1).size === 0 && typeof blob.stream === 'function';
}

async function uploadWebFileViaStream({
  uploadUrl,
  blob,
  mimeType,
  total,
  onProgress,
  chunkSize,
}) {
  const reader = blob.stream().getReader();
  let offset = 0;
  let pending = new Uint8Array(0);

  while (offset < total) {
    const end = Math.min(offset + chunkSize, total) - 1;
    const need = end - offset + 1;

    while (pending.length < need) {
      const { done, value } = await reader.read();
      if (done) break;
      if (!value?.byteLength) continue;
      const merged = new Uint8Array(pending.length + value.byteLength);
      merged.set(pending);
      merged.set(value, pending.length);
      pending = merged;
    }

    if (pending.length < need) {
      throw new Error('Video file is empty.');
    }

    const chunkBytes = pending.slice(0, need);
    pending = pending.slice(need);

    const data = await postResumableChunk({
      uploadUrl,
      mimeType,
      offset,
      end,
      total,
      body: chunkBytes,
    });

    if (data.status === 308) {
      offset =
        typeof data.nextOffset === 'number' && data.nextOffset > offset
          ? data.nextOffset
          : end + 1;
      onProgress?.({ loaded: offset, total });
      continue;
    }

    if (data.success && data.videoId) {
      onProgress?.({ loaded: total, total });
      return {
        videoId: data.videoId,
        youtubeUrl: data.youtubeUrl || `https://www.youtube.com/watch?v=${data.videoId}`,
        uploadDate: data.uploadDate || new Date().toISOString().split('T')[0],
      };
    }

    throw new Error(data.message || data.error || 'Video upload did not complete.');
  }

  throw new Error('Video upload did not complete.');
}

async function postResumableChunkViaNativeUpload({
  method = YOUTUBE_UPLOAD_METHODS.NATIVE_RESUMABLE,
  uploadUrl,
  mimeType,
  offset,
  end,
  total,
  chunkFileUri,
  chunkIndex,
}) {
  const chunkApi = getYoutubeUploadChunkApiBase();
  if (!chunkApi) {
    throw new Error('Video upload API URL is not configured.');
  }

  const chunkUrl = `${chunkApi}/api/youtube/upload-chunk`;
  let lastErr;
  for (let attempt = 0; attempt < MAX_CHUNK_RETRIES; attempt += 1) {
    try {
      if (attempt > 0 || chunkIndex === 1) {
        logYoutubeUploadPhase(method, 'resumable-chunk-native', {
          chunkIndex,
          attempt: attempt + 1,
          chunkUrl,
          chunkApiBase: chunkApi,
          contentRange: `bytes ${offset}-${end}/${total}`,
          transport: 'FileSystem.uploadAsync',
        });
      }
      const result = await FileSystem.uploadAsync(
        chunkUrl,
        chunkFileUri,
        {
          httpMethod: 'POST',
          uploadType: FileSystem.FileSystemUploadType.BINARY_CONTENT,
          headers: {
            'Content-Type': mimeType,
            'X-Youtube-Upload-Url': uploadUrl,
            'Content-Range': `bytes ${offset}-${end}/${total}`,
          },
        }
      );

      const data = await parseJsonResponseFromText(result.body);
      if (result.status < 200 || result.status >= 300) {
        const err = new Error(
          formatYoutubeUploadError(data, 'Video upload failed', result.status)
        );
        logYoutubeUploadError(method, 'resumable-chunk-native-http', err, {
          chunkIndex,
          attempt: attempt + 1,
          chunkUrl,
          httpStatus: result.status,
          responseBodyPreview: String(result.body || '').slice(0, 200),
        });
        throw err;
      }
      if (chunkIndex === 1 || chunkIndex % 10 === 0 || data.success) {
        logYoutubeUploadPhase(method, 'resumable-chunk-native-ok', {
          chunkIndex,
          httpStatus: result.status,
          youtubeStatus: data.status,
          nextOffset: data.nextOffset,
          success: data.success,
        });
      }
      return data;
    } catch (err) {
      lastErr = err;
      logYoutubeUploadError(method, 'resumable-chunk-native', err, {
        chunkIndex,
        attempt: attempt + 1,
        chunkUrl,
        contentRange: `bytes ${offset}-${end}/${total}`,
      });
      const msg = err?.message || String(err);
      const retryable =
        isRawNetworkFailure(err) ||
        /network|failed to fetch|load failed|timed out|timeout/i.test(msg);
      if (!retryable || attempt === MAX_CHUNK_RETRIES - 1) {
        if (isRawNetworkFailure(err) || /network|load failed/i.test(msg)) {
          throw new Error(
            'Lost connection while uploading the video. Check your network and try again.'
          );
        }
        throw err;
      }
      await new Promise((r) => setTimeout(r, 800 * (attempt + 1)));
    }
  }
  throw lastErr || new Error('Video upload failed');
}

/**
 * Native: stream a local video file to YouTube via resumable upload (upload-init + upload-chunk).
 * Uses FileSystem.uploadAsync (NSURLSession / OkHttp) for reliable chunk POSTs on mobile.
 */
export async function uploadNativeUriToYoutubeResumable({
  method = YOUTUBE_UPLOAD_METHODS.NATIVE_RESUMABLE,
  readUri,
  totalBytes,
  mimeType = 'video/mp4',
  uploadUrl,
  onProgress,
}) {
  const chunkSize = getChunkSize();
  let offset = 0;
  let chunkIndex = 0;
  const totalChunks = Math.ceil(totalBytes / chunkSize);

  logYoutubeUploadPhase(method, 'resumable-chunks-start', {
    totalBytes,
    totalSize: formatBytes(totalBytes),
    chunkSize,
    chunkSizeLabel: formatBytes(chunkSize),
    totalChunks,
    chunkApiBase: getYoutubeUploadChunkApiBase(),
  });

  while (offset < totalBytes) {
    chunkIndex += 1;
    const length = Math.min(chunkSize, totalBytes - offset);
    const end = offset + length - 1;
    const base64Chunk = await FileSystem.readAsStringAsync(readUri, {
      encoding: FileSystem.EncodingType.Base64,
      position: offset,
      length,
    });
    if (!base64Chunk) {
      const err = new Error('Could not read the selected video. Please choose it again.');
      logYoutubeUploadError(method, 'read-chunk', err, { chunkIndex, offset, length });
      throw err;
    }

    const chunkPath = `${FileSystem.cacheDirectory}yt_chunk_${offset}_${Date.now()}.bin`;
    await FileSystem.writeAsStringAsync(chunkPath, base64Chunk, {
      encoding: FileSystem.EncodingType.Base64,
    });

    let data;
    try {
      data = await postResumableChunkViaNativeUpload({
        method,
        uploadUrl,
        mimeType,
        offset,
        end,
        total: totalBytes,
        chunkFileUri: chunkPath,
        chunkIndex,
      });
    } finally {
      try {
        await FileSystem.deleteAsync(chunkPath, { idempotent: true });
      } catch {
        /* ignore */
      }
    }

    if (data.status === 308) {
      offset =
        typeof data.nextOffset === 'number' && data.nextOffset > offset
          ? data.nextOffset
          : end + 1;
      onProgress?.({ loaded: offset, total: totalBytes });
      continue;
    }

    if (data.success && data.videoId) {
      logYoutubeUploadPhase(method, 'resumable-chunks-complete', {
        chunksSent: chunkIndex,
        totalChunks,
        videoId: data.videoId,
      });
      onProgress?.({ loaded: totalBytes, total: totalBytes });
      return {
        videoId: data.videoId,
        youtubeUrl: data.youtubeUrl || `https://www.youtube.com/watch?v=${data.videoId}`,
        uploadDate: data.uploadDate || new Date().toISOString().split('T')[0],
      };
    }

    const err = new Error(data.message || data.error || 'Video upload did not complete.');
    logYoutubeUploadError(method, 'resumable-chunk-unexpected', err, {
      chunkIndex,
      offset,
      response: data,
    });
    throw err;
  }

  const err = new Error('Video upload did not complete.');
  logYoutubeUploadError(method, 'resumable-chunks-incomplete', err, {
    chunksSent: chunkIndex,
    totalChunks,
    finalOffset: offset,
  });
  throw err;
}

/**
 * Web File → YouTube resumable upload via slice()+arrayBuffer() reads (mobile-safe).
 * Chunks go to our API as raw binary with Content-Range (YouTube resumable protocol).
 */
export async function uploadWebFileToYoutubeResumable({
  method = YOUTUBE_UPLOAD_METHODS.WEB_MOBILE_RESUMABLE,
  file,
  totalBytes,
  mimeType = 'video/mp4',
  uploadUrl,
  onProgress,
}) {
  const chunkSize = getChunkSize();
  let offset = 0;
  let chunkIndex = 0;
  const totalChunks = Math.ceil(totalBytes / chunkSize);

  logYoutubeUploadPhase(method, 'resumable-chunks-start', {
    totalBytes,
    totalSize: formatBytes(totalBytes),
    chunkSize,
    chunkSizeLabel: formatBytes(chunkSize),
    totalChunks,
    chunkApiBase: getYoutubeUploadChunkApiBase(),
    readMode: 'slice-arrayBuffer',
  });

  while (offset < totalBytes) {
    chunkIndex += 1;
    const end = Math.min(offset + chunkSize, totalBytes) - 1;
    const slice = file.slice(offset, end + 1);
    let chunkBody;
    try {
      chunkBody = await slice.arrayBuffer();
    } catch (err) {
      logYoutubeUploadError(method, 'read-slice', err, { chunkIndex, offset, end });
      throw new Error('Could not read the selected video. Please choose it again.');
    }

    const data = await postResumableChunk({
      method,
      uploadUrl,
      mimeType,
      offset,
      end,
      total: totalBytes,
      body: chunkBody,
      chunkIndex,
    });

    if (data.status === 308) {
      offset =
        typeof data.nextOffset === 'number' && data.nextOffset > offset
          ? data.nextOffset
          : end + 1;
      onProgress?.({ loaded: offset, total: totalBytes });
      continue;
    }

    if (data.success && data.videoId) {
      logYoutubeUploadPhase(method, 'resumable-chunks-complete', {
        chunksSent: chunkIndex,
        totalChunks,
        videoId: data.videoId,
      });
      onProgress?.({ loaded: totalBytes, total: totalBytes });
      return {
        videoId: data.videoId,
        youtubeUrl: data.youtubeUrl || `https://www.youtube.com/watch?v=${data.videoId}`,
        uploadDate: data.uploadDate || new Date().toISOString().split('T')[0],
      };
    }

    const err = new Error(data.message || data.error || 'Video upload did not complete.');
    logYoutubeUploadError(method, 'resumable-chunk-unexpected', err, {
      chunkIndex,
      offset,
      response: data,
    });
    throw err;
  }

  const err = new Error('Video upload did not complete.');
  logYoutubeUploadError(method, 'resumable-chunks-incomplete', err, {
    chunksSent: chunkIndex,
    totalChunks,
    finalOffset: offset,
  });
  throw err;
}

/**
 * Upload a Blob/File via chunked resumable upload (client → our API → YouTube).
 */
export async function uploadBlobToYoutubeResumable({
  uploadUrl,
  blob,
  mimeType = 'video/mp4',
  totalSize,
  onProgress,
}) {
  const total = totalSize > 0 ? totalSize : blob.size;
  if (!total) {
    throw new Error('Video file is empty.');
  }

  const chunkSize = getChunkSize();

  if (chunkBodyNeedsStream(blob, total, chunkSize)) {
    return uploadWebFileViaStream({
      uploadUrl,
      blob,
      mimeType,
      total,
      onProgress,
      chunkSize,
    });
  }

  let offset = 0;
  while (offset < total) {
    const end = Math.min(offset + chunkSize, total) - 1;
    const chunk = blob.slice(offset, end + 1);

    const data = await postResumableChunk({
      uploadUrl,
      mimeType,
      offset,
      end,
      total,
      body: chunk,
    });

    if (data.status === 308) {
      offset =
        typeof data.nextOffset === 'number' && data.nextOffset > offset
          ? data.nextOffset
          : end + 1;
      onProgress?.({ loaded: offset, total });
      continue;
    }

    if (data.success && data.videoId) {
      onProgress?.({ loaded: total, total });
      return {
        videoId: data.videoId,
        youtubeUrl: data.youtubeUrl || `https://www.youtube.com/watch?v=${data.videoId}`,
        uploadDate: data.uploadDate || new Date().toISOString().split('T')[0],
      };
    }

    throw new Error(data.message || data.error || 'Video upload did not complete.');
  }

  throw new Error('Video upload did not complete.');
}
